/** @format */

import React, { useEffect, useReducer, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import FloatingLabel from "react-bootstrap/FloatingLabel";
import { ReactComponent as Plus } from "bootstrap-icons/icons/plus-lg.svg";
import { ReactComponent as Pencil } from "bootstrap-icons/icons/pencil.svg";
import { ReactComponent as Floppy } from "bootstrap-icons/icons/floppy.svg";
import { ReactComponent as Trash } from "bootstrap-icons/icons/trash.svg";
import { ReactComponent as ChevronUp } from "bootstrap-icons/icons/chevron-up.svg";
import { ReactComponent as ChevronDown } from "bootstrap-icons/icons/chevron-down.svg";
import "./ResourceLinks.scss";
import { ResourceLink } from "../../../types";
import HelperTooltip from "../helperTooltip/HelperTooltip";
import {
  DEFAULT_CATEGORY_NAME,
  getLinkOffset,
  initialLinksState,
  ResourceLinksReducer,
} from "./ResourceLinksReducer";
import AddLink from "./AddLink";
import ResourceLinksOrder from "./ResourceLinksOrder";
import { isSecureLink } from "../../../utils/functions";

type Props = {
  saveLinks: (links: ResourceLink[]) => Promise<boolean> | void;
  buttonText: string;
  propDefaultResourceLinks: ResourceLink[] | undefined;
  closeModal?: () => void;
};

enum FormModeEnum {
  EditData = "EditData",
  EditOrder = "EditOrder",
}

const defaultCategoriesTemp = {
  catLoc: -1, // An index never in the categories array
  catTempVal: "",
};

const ResourceLinks: React.FC<Props> = ({
  saveLinks,
  buttonText,
  propDefaultResourceLinks,
  closeModal,
}) => {
  const { t: _ } = useTranslation();

  const [state, dispatch] = useReducer(ResourceLinksReducer, initialLinksState);
  const [mode, setMode] = useState<FormModeEnum>(FormModeEnum.EditData);
  const [categoriesTemp, setCategoriesTemp] = useState<{
    catLoc: number;
    catTempVal: string;
  }>(defaultCategoriesTemp);
  const [linksTemp, setLinksTemp] = useState<ResourceLink[]>();
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const isModal = closeModal !== undefined;

  const duplicateCategoryExists = (
    categories: string[],
    catName: string,
    catIndex: number,
  ): boolean => {
    return (
      categories.filter((cat, i) => cat === catName && i !== catIndex).length >
      0
    );
  };

  const areCategoriesValid = (): boolean => {
    let invalid = false;
    state.categories.forEach((cat, i) => {
      if (duplicateCategoryExists(state.categories, cat, i)) {
        invalid = true;
        return;
      }
      if (cat === "") {
        invalid = true;
        return;
      }
    });

    return !invalid;
  };

  const areLinksValid = (): boolean => {
    if (state.links.length === 0) return true;
    return (
      state.links.findIndex(
        (link) =>
          link.label === "" || link.src === "" || !isSecureLink(link.src),
      ) === -1
    );
  };

  const isFormValid = (): boolean => {
    return areCategoriesValid() && areLinksValid();
  };

  useEffect(() => {
    if (!propDefaultResourceLinks || propDefaultResourceLinks.length === 0)
      return;

    dispatch({
      type: "freshLinks",
      payload: { newLinks: propDefaultResourceLinks },
    });
  }, [propDefaultResourceLinks]);

  const resourceLinks = (
    <div className="resource-links">
      <h5 className="mt-3 mb-2">
        {_("Categories")}
        <HelperTooltip tooltipText={_("linkCategories")} />
      </h5>
      <>
        {state.categories &&
          state.categories.map((category, catIndex) => (
            <div className="border rounded p-2 pt-3 mb-3" key={catIndex}>
              <Form.Group className="category">
                <FloatingLabel controlId="floatingInput" label={_("Title")}>
                  <Form.Control
                    placeholder=""
                    name="categoryName"
                    aria-describedby="Category input"
                    aria-label="Category input"
                    data-testid="catInp"
                    role="input"
                    disabled={categoriesTemp.catLoc !== catIndex}
                    value={
                      categoriesTemp.catLoc === catIndex
                        ? categoriesTemp.catTempVal
                        : category === DEFAULT_CATEGORY_NAME // translate hardcoded none
                          ? _(DEFAULT_CATEGORY_NAME)
                          : category
                    }
                    isInvalid={
                      categoriesTemp.catLoc === catIndex &&
                      duplicateCategoryExists(
                        // if in edit mode and cat invalid disable this button
                        state.categories,
                        categoriesTemp.catTempVal,
                        catIndex,
                      )
                    }
                    onChange={(e) => {
                      setCategoriesTemp({
                        catLoc: catIndex,
                        catTempVal: e.target.value,
                      });
                    }}
                  />
                </FloatingLabel>
                <Button
                  className={`action-button ${category === DEFAULT_CATEGORY_NAME && catIndex === 0 ? "d-none" : ""}`}
                  title={categoriesTemp.catLoc !== catIndex ? _("Edit category") : _("Save category")}
                  data-testid="editCat"
                  disabled={
                    (categoriesTemp.catLoc !== catIndex && // if in edit mode and not being edited
                      categoriesTemp.catLoc !== defaultCategoriesTemp.catLoc) ||
                    duplicateCategoryExists(
                      // if in edit mode and cat invalid disable this button
                      state.categories,
                      categoriesTemp.catTempVal,
                      catIndex,
                    )
                  }
                  onClick={() => {
                    // if not in edit mode, switch this input to not disabled and have its value saved to a temp state
                    if (
                      categoriesTemp.catLoc === defaultCategoriesTemp.catLoc
                    ) {
                      setCategoriesTemp({
                        catLoc: catIndex,
                        catTempVal: category,
                      });
                    }
                    // if in edit mode, save category title
                    if (categoriesTemp.catLoc === catIndex) {
                      dispatch({
                        type: "updateCategory",
                        payload: {
                          categoryIndex: categoriesTemp.catLoc,
                          newCategoryName: categoriesTemp.catTempVal,
                        },
                      });
                      setCategoriesTemp(defaultCategoriesTemp);
                    }
                  }}
                >
                  {categoriesTemp.catLoc !== catIndex ? <Pencil /> : <Floppy />}
                </Button>
                <Button
                  className={`action-button ${category === DEFAULT_CATEGORY_NAME && catIndex === 0 ? "d-none" : ""}`}
                  title={_("Delete category")}
                  data-testid="deleteCat"
                  onClick={() => {
                    dispatch({
                      type: "deleteCategory",
                      payload: { categoryIndex: catIndex },
                    });
                  }}
                >
                  <Trash />
                </Button>
              </Form.Group>
              {categoriesTemp.catLoc === defaultCategoriesTemp.catLoc && ( // hide links while a cat title is being edited
                <AddLink
                  disabled={
                    duplicateCategoryExists(
                      state.categories,
                      category,
                      catIndex,
                    ) || category === ""
                  }
                  category={category}
                  categoryOffset={getLinkOffset(catIndex, state)}
                  files={state.links.filter(
                    (link) => link.category === category,
                  )}
                  // pass the action to the reducer
                  dispatch={(action) => dispatch(action)}
                />
              )}
            </div>
          ))}

        <Button
          variant="editing"
          onClick={() =>
            dispatch({
              type: "newCategory",
            })
          }
          data-testid="addCat"
        >
          {_("Add category")} <Plus />
        </Button>
      </>
    </div>
  );

  if (isModal) {
    return (
      <>
        <Modal.Body>
          {mode === FormModeEnum.EditData ? (
            resourceLinks
          ) : (
            <ResourceLinksOrder
              linksOriginal={state.links}
              updateLinks={undefined}
              watchLinks={(links) => {
                setLinksTemp(links);
              }}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="buttons-container justify-content-end">
            <div className="form-action-buttons d-flex gap-2">
              {state.links.length > 0 && mode === FormModeEnum.EditData && (
                <Button
                  variant="editing"
                  onClick={() => {
                    setMode(FormModeEnum.EditOrder);
                  }}
                  disabled={!isFormValid()}
                >
                  {_("Edit order")} <ChevronUp /> <ChevronDown />
                </Button>
              )}

              <Button
                variant="outline-editing"
                onClick={() => closeModal !== undefined && closeModal()}
              >
                {_("Cancel")}
              </Button>

              {mode === FormModeEnum.EditData ? (
                <Button
                  aria-label="save"
                  className="outline-editing"
                  variant={`${isSaving ? "outline-editing" : "editing"}`}
                  type="submit"
                  onClick={async () => {
                    setIsSaving(true);
                    await saveLinks(state.links);
                    setIsSaving(false);
                    closeModal !== undefined && closeModal();
                  }}
                  disabled={!isFormValid()}
                >
                  {isSaving ? <Spinner /> : _("Save")}
                </Button>
              ) : (
                <Button
                  variant="editing"
                  onClick={() => {
                    if (linksTemp !== undefined) {
                      dispatch({
                        type: "freshLinks",
                        payload: { newLinks: linksTemp },
                      });
                    }
                    setLinksTemp(undefined);
                    setMode(FormModeEnum.EditData);
                  }}
                >
                  {_("Edit links")}
                </Button>
              )}
            </div>
          </div>
        </Modal.Footer>
      </>
    );
  } else {
    return (
      <>
        {mode === FormModeEnum.EditData ? (
          resourceLinks
        ) : (
          <ResourceLinksOrder
            linksOriginal={state.links}
            updateLinks={(links) => {
              dispatch({ type: "freshLinks", payload: { newLinks: links } });
              setMode(FormModeEnum.EditData);
            }}
          />
        )}

        <hr className="my-3" />

        {state.links.length > 0 && mode === FormModeEnum.EditData && (
          <div className="mb-4">
            <Button
              variant="editing"
              onClick={() => setMode(FormModeEnum.EditOrder)}
              disabled={!isFormValid()}
            >
              {_("Edit order")} <ChevronUp /> <ChevronDown />
            </Button>
          </div>
        )}

        <Button
          onClick={() => saveLinks(state.links)}
          data-testid="saveLinks"
          disabled={!isFormValid()}
          className={buttonText === _("Next") ? "navigation-button" : ""}
        >
          {buttonText}
        </Button>
      </>
    );
  }
};

export default ResourceLinks;
